import { useMemo, useState } from 'react';
import type { StyleConfig } from '../../../../types/bi';
import styles from './PivotTable.module.css';

interface PivotTableProps {
  data: { columns: string[]; rows: Record<string, unknown>[] };
  dimensions: string[];
  measures: { alias: string }[];
  style?: StyleConfig;
}

/**
 * Cross-tab pivot table.
 *
 * - First dimension → row grouping
 * - Second dimension (optional) → column pivot headers
 * - Measures → aggregated cell values (SUM)
 */
export default function PivotTable({ data, dimensions, measures, style: _style }: PivotTableProps) {
  const rowDim = dimensions[0] || data.columns[0];
  const colDim = dimensions.length >= 2 ? dimensions[1] : null;
  const measureKeys = measures.length
    ? measures.map((m) => m.alias)
    : data.columns.filter((c) => c !== rowDim && c !== colDim);

  // Collapsed row groups
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());

  const { pivotColumns, rowGroups, grandTotals } = useMemo(() => {
    // Determine unique pivot column values (sorted)
    const pivotCols = colDim
      ? Array.from(new Set(data.rows.map((r) => String(r[colDim] ?? '')))).sort()
      : null;

    // Group rows by row dimension
    const groups = new Map<string, Map<string, number[]>>();
    for (const row of data.rows) {
      const rowKey = String(row[rowDim] ?? '');
      if (!groups.has(rowKey)) groups.set(rowKey, new Map());
      const group = groups.get(rowKey)!;

      const colKey = colDim ? String(row[colDim] ?? '') : '__all__';
      if (!group.has(colKey))
        group.set(
          colKey,
          measureKeys.map(() => 0),
        );
      const vals = group.get(colKey)!;
      measureKeys.forEach((mk, mi) => {
        vals[mi] += Number(row[mk]) || 0;
      });
    }

    // Grand totals
    const totals = new Map<string, number[]>();
    for (const [, group] of groups) {
      for (const [colKey, vals] of group) {
        if (!totals.has(colKey))
          totals.set(
            colKey,
            measureKeys.map(() => 0),
          );
        const t = totals.get(colKey)!;
        vals.forEach((v, i) => {
          t[i] += v;
        });
      }
    }

    return {
      pivotColumns: pivotCols,
      rowGroups: groups,
      grandTotals: totals,
    };
  }, [data.rows, rowDim, colDim, measureKeys]);

  if (!data.rows.length) {
    return <div className={styles.empty}>No data</div>;
  }

  function toggleGroup(key: string) {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  const fmt = (v: number) =>
    Number.isInteger(v)
      ? v.toLocaleString()
      : v.toLocaleString(undefined, { maximumFractionDigits: 2 });

  // Build column headers
  const headerCells: { label: string; colSpan: number }[] = [];
  if (pivotColumns) {
    headerCells.push({ label: rowDim, colSpan: 1 });
    for (const pc of pivotColumns) {
      headerCells.push({ label: pc, colSpan: measureKeys.length });
    }
    headerCells.push({ label: 'Total', colSpan: measureKeys.length });
  }

  // Non-pivoted mode (single dimension)
  if (!pivotColumns) {
    const sortedGroups = Array.from(rowGroups.entries()).sort(([a], [b]) => a.localeCompare(b));
    return (
      <div className={styles.wrapper}>
        <div className={styles.tableScroll}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th className={styles.th}>{rowDim}</th>
                {measureKeys.map((mk) => (
                  <th key={mk} className={`${styles.th} ${styles.thMeasure}`}>
                    {mk}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sortedGroups.map(([key, group], i) => {
                const vals = group.get('__all__') || measureKeys.map(() => 0);
                return (
                  <tr key={key} className={i % 2 === 0 ? styles.evenRow : ''}>
                    <td className={styles.td}>{key}</td>
                    {vals.map((v, mi) => (
                      <td key={mi} className={`${styles.td} ${styles.tdMeasure}`}>
                        {fmt(v)}
                      </td>
                    ))}
                  </tr>
                );
              })}
              <tr className={styles.grandTotal}>
                <td className={styles.td}>Grand Total</td>
                {(grandTotals.get('__all__') || measureKeys.map(() => 0)).map((v, mi) => (
                  <td key={mi} className={`${styles.td} ${styles.tdMeasure}`}>
                    {fmt(v)}
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  // Pivoted mode
  const sortedGroups = Array.from(rowGroups.entries()).sort(([a], [b]) => a.localeCompare(b));

  return (
    <div className={styles.wrapper}>
      <div className={styles.tableScroll}>
        <table className={styles.table}>
          <thead>
            {/* Top header: group labels spanning measures */}
            <tr>
              {headerCells.map((cell, i) => (
                <th
                  key={i}
                  className={`${styles.th} ${i > 0 ? styles.thMeasure : ''}`}
                  colSpan={cell.colSpan}
                >
                  {cell.label}
                </th>
              ))}
            </tr>
            {/* Sub-header: measure names */}
            {measureKeys.length > 1 && (
              <tr>
                <th className={styles.th} />
                {[...pivotColumns, 'Total'].map((pc) =>
                  measureKeys.map((mk) => (
                    <th key={`${pc}-${mk}`} className={`${styles.th} ${styles.thMeasure}`}>
                      {mk}
                    </th>
                  )),
                )}
              </tr>
            )}
          </thead>
          <tbody>
            {sortedGroups.map(([key, group], gi) => {
              const isCollapsed = collapsed.has(key);
              // Row totals
              const rowTotals = measureKeys.map(() => 0);
              for (const [, vals] of group) {
                vals.forEach((v, i) => {
                  rowTotals[i] += v;
                });
              }

              return (
                <tr
                  key={key}
                  className={`${gi % 2 === 0 ? styles.evenRow : ''} ${isCollapsed ? styles.groupRow : ''}`}
                  onClick={() => toggleGroup(key)}
                  style={{ cursor: 'pointer' }}
                >
                  <td className={styles.td}>
                    <button
                      className={styles.toggleBtn}
                      aria-label={isCollapsed ? 'Expand' : 'Collapse'}
                    >
                      {isCollapsed ? '▶' : '▼'}
                    </button>
                    {key}
                  </td>
                  {pivotColumns.map((pc) => {
                    const vals = group.get(pc) || measureKeys.map(() => 0);
                    return measureKeys.map((_, mi) => (
                      <td key={`${pc}-${mi}`} className={`${styles.td} ${styles.tdMeasure}`}>
                        {isCollapsed ? '' : fmt(vals[mi])}
                      </td>
                    ));
                  })}
                  {rowTotals.map((v, mi) => (
                    <td key={`total-${mi}`} className={`${styles.td} ${styles.tdMeasure}`}>
                      {fmt(v)}
                    </td>
                  ))}
                </tr>
              );
            })}
            {/* Grand total row */}
            <tr className={styles.grandTotal}>
              <td className={styles.td}>Grand Total</td>
              {pivotColumns.map((pc) => {
                const vals = grandTotals.get(pc) || measureKeys.map(() => 0);
                return measureKeys.map((_, mi) => (
                  <td key={`gt-${pc}-${mi}`} className={`${styles.td} ${styles.tdMeasure}`}>
                    {fmt(vals[mi])}
                  </td>
                ));
              })}
              {measureKeys
                .map((_, mi) => {
                  let total = 0;
                  for (const [, vals] of grandTotals) total += vals[mi];
                  return total;
                })
                .map((v, mi) => (
                  <td key={`gtt-${mi}`} className={`${styles.td} ${styles.tdMeasure}`}>
                    {fmt(v)}
                  </td>
                ))}
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}
